/**
 * ScreenPickerDialog — click-to-add screen picker for the V2 canvas.
 *
 * An alternative to drag-and-drop from the template panel: shows the available
 * templates as scaled phone tiles (ScreenGridPanel) with an auto-focused search
 * and a persisted size slider, grouped by functional category. Clicking a tile
 * adds that screen to the canvas at the viewport center.
 *
 * "Close after adding" (persisted, default on): on click the dialog shows a
 * brief loading state, then closes — the parent selects the new screen. When
 * off, the dialog stays open for multi-add and shows an "Added …" toast.
 */

import React, { useMemo, useState } from 'react';
import {
  Snackbar,
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SDialog } from '@modules/ui-components';
import { ScreenGridPanel, groupByStepKindCategory } from '@modules/themes/components';
import { usePersistentBoolean } from '@modules/themes/hooks';
import type { StepNode } from '@segmently/schema-models/v2';

export interface ScreenPickerDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Templates to choose from (theme flowDocument screens or default templates). */
  screens: StepNode[];
  projectId?: string;
  locale?: string;
  searchPlaceholder?: string;
  selectedKeys?: string[];
  showCloseAfterToggle?: boolean;
  toastVerb?: string;
  /** Add/select the chosen template. */
  onPick: (screen: StepNode) => void | Promise<void>;
  /**
   * Opt-in: clone a template tile into the theme as a NEW subtype. The dialog
   * captures + validates the subtype label (non-empty, unique per StepKind) and
   * calls this with the source template and the validated subtype.
   */
  onCloneAsSubtype?: (source: StepNode, subtype: string) => void | Promise<void>;
}

function formatStepKind(screenType: string): string {
  return screenType.replace(/([A-Z])/g, ' $1').trim();
}

export const ScreenPickerDialog: React.FC<ScreenPickerDialogProps> = ({
  open,
  onClose,
  title = 'Add screen',
  screens,
  projectId,
  locale = 'en-US',
  searchPlaceholder = 'Search screens to add',
  selectedKeys,
  showCloseAfterToggle = true,
  toastVerb = 'Added',
  onPick,
  onCloneAsSubtype,
}) => {
  const [added, setAdded] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [closeAfterAdd, setCloseAfterAdd] = usePersistentBoolean('screen-picker:close-after-add', true);

  // Clone-as-subtype sub-dialog state.
  const [cloneSource, setCloneSource] = useState<StepNode | null>(null);
  const [subtypeInput, setSubtypeInput] = useState('');
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [cloning, setCloning] = useState(false);

  // Existing subtypes already used for the source's StepKind — enforce uniqueness.
  const existingSubtypes = useMemo(() => {
    if (!cloneSource) return new Set<string>();
    return new Set(
      screens
        .filter((s) => s.screenType === cloneSource.screenType)
        .map((s) => (s as unknown as { subtype?: string }).subtype?.trim())
        .filter((v): v is string => Boolean(v)),
    );
  }, [screens, cloneSource]);

  const openCloneDialog = (source: StepNode) => {
    setCloneSource(source);
    setSubtypeInput('');
    setCloneError(null);
  };

  const handleConfirmSubtype = async () => {
    const value = subtypeInput.trim();
    if (!value) {
      setCloneError('Enter a subtype name');
      return;
    }
    if (existingSubtypes.has(value)) {
      setCloneError(`Subtype “${value}” already exists for this screen type`);
      return;
    }
    if (!cloneSource || !onCloneAsSubtype) return;
    setCloning(true);
    try {
      await onCloneAsSubtype(cloneSource, value);
      const label = `${formatStepKind(cloneSource.screenType as string)} · ${value}`;
      setCloneSource(null);
      setSubtypeInput('');
      setCloneError(null);
      setAdded(label);
    } catch {
      setCloneError('Failed to save the new subtype');
    } finally {
      setCloning(false);
    }
  };

  const handlePick = async (screen: StepNode) => {
    if (adding) return;
    setAdding(true);
    try {
      await onPick(screen);
    } finally {
      setAdding(false);
    }
    if (closeAfterAdd) {
      onClose();
      return;
    }
    const name =
      (screen as unknown as { name?: string }).name ?? formatStepKind(screen.screenType as string);
    setAdded(name);
  };

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="lg"
      fullWidth
      slotProps={{ paper: { sx: { height: '85vh', maxHeight: '85vh' } } }}
      data-testid="screen-picker-dialog"
    >
      <Stack spacing={1} sx={{ height: '100%', minHeight: 0, position: 'relative' }}>
        {showCloseAfterToggle && (
          <FormControlLabel
            sx={{ alignSelf: 'flex-end', mr: 1, flexShrink: 0 }}
            control={
              <Checkbox
                size="small"
                checked={closeAfterAdd}
                onChange={(e) => setCloseAfterAdd(e.target.checked)}
                data-testid="screen-picker-close-after-toggle"
              />
            }
            label={<Typography variant="body2">Close after adding</Typography>}
          />
        )}
        <ScreenGridPanel
          screens={screens}
          projectId={projectId}
          locale={locale}
          scaleStorageKey="screen-picker:scale"
          defaultScale={0.5}
          showSearch
          autoFocusSearch
          searchPlaceholder={searchPlaceholder}
          groupBy={groupByStepKindCategory}
          onScreenClick={handlePick}
          selectedKeys={selectedKeys}
          onCloneAsSubtype={onCloneAsSubtype ? openCloneDialog : undefined}
        />
        <Backdrop
          open={adding}
          sx={{ position: 'absolute', inset: 0, zIndex: (t) => t.zIndex.modal + 1, color: '#fff' }}
          data-testid="screen-picker-adding-overlay"
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Stack>
      <Snackbar
        open={Boolean(added)}
        autoHideDuration={2000}
        onClose={() => setAdded(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" data-testid="screen-picker-added-toast">
          {toastVerb} “{added}”
        </Alert>
      </Snackbar>

      {/* Clone-as-subtype sub-dialog */}
      <SDialog
        open={Boolean(cloneSource)}
        onClose={() => {
          if (!cloning) setCloneSource(null);
        }}
        title="Clone as new subtype"
        maxWidth="xs"
        fullWidth
        data-testid="screen-picker-subtype-dialog"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Adds a new{' '}
            {cloneSource ? formatStepKind(cloneSource.screenType as string) : ''} template
            to this onboarding’s theme. You can then tune it independently.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Subtype"
            placeholder="e.g. quiz, premium"
            value={subtypeInput}
            onChange={(e) => {
              setSubtypeInput(e.target.value);
              if (cloneError) setCloneError(null);
            }}
            error={Boolean(cloneError)}
            helperText={cloneError ?? 'A short label, unique for this screen type'}
            data-testid="screen-picker-subtype-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleConfirmSubtype();
              }
            }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              onClick={() => setCloneSource(null)}
              disabled={cloning}
              data-testid="screen-picker-subtype-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleConfirmSubtype()}
              disabled={cloning}
              data-testid="screen-picker-subtype-confirm"
            >
              {cloning ? 'Adding…' : 'Create template'}
            </Button>
          </Stack>
        </Stack>
      </SDialog>
    </SDialog>
  );
};

export default ScreenPickerDialog;
